import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, FileCheck2, Compass, Gift, Check, Linkedin, UserRound } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { StartCta } from "./SiteChrome";
import { FreeCheckCard, useFreeCheck } from "./FreeCheck";
import FreeCheckResult from "./FreeCheckResult";
import { GUEST_CLAIM_KEY, GUEST_KEY } from "../studio/constants";

const BENEFITS = [
  { icon: FileCheck2, label: "ATS-Friendly CVs" },
  { icon: Compass, label: "Personalised Career Guidance" },
  { icon: Gift, label: "Free Core Tools" },
];

export const Hero = () => {
  const fc = useFreeCheck();
  const navigate = useNavigate();
  const { user } = useAuth();
  const prev = useRef(fc.state);
  const { setMode } = fc;
  useEffect(() => {
    const open = () => { setMode("profile"); setTimeout(() => document.getElementById("free-check")?.scrollIntoView({ behavior: "smooth", block: "start" }), 60); };
    if (window.location.hash === "#create-profile") open();
    window.addEventListener("gi-create-profile", open);
    return () => window.removeEventListener("gi-create-profile", open);
  }, [setMode]);
  useEffect(() => {
    if (fc.state === "done" && prev.current === "processing" && fc.result) {
      setTimeout(() => document.getElementById("free-check-result")?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
    prev.current = fc.state;
  }, [fc.state, fc.result]);
  const save = () => {
    const t = localStorage.getItem(GUEST_KEY);
    if (t) localStorage.setItem(GUEST_CLAIM_KEY, t);
    navigate(user ? "/dashboard" : "/signin");
  };
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 af-grid opacity-60 [mask-image:radial-gradient(ellipse_at_top_right,black,transparent_60%)] pointer-events-none" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 pt-7 sm:pt-16 pb-14 sm:pb-24 grid lg:grid-cols-2 lg:grid-rows-[auto_1fr] gap-x-16 gap-y-7 sm:gap-y-10 lg:gap-y-8">
        <div className="af-fade-up lg:self-end">
          <h1 data-testid="hero-heading" className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-ink">
            Your Next Interview Starts Here.
          </h1>
          <p data-testid="hero-subheading" className="mt-4 sm:mt-5 text-base sm:text-lg text-body leading-relaxed max-w-xl">
            Check your CV for free, see how well you fit a job, then tailor your real experience to it.
          </p>
        </div>
        <div className="af-fade-up min-w-0 lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:self-center" style={{ animationDelay: "0.1s" }}>
          <FreeCheckCard fc={fc} />
        </div>
        <div className="af-fade-up lg:self-start">
          <div className="flex flex-col sm:flex-row gap-3">
            <StartCta testid="hero-primary-cta">Get Started Free</StartCta>
            <a data-testid="hero-secondary-cta" href="#how" className="gi-btn gi-btn-secondary gi-btn-lg">See How It Works</a>
          </div>
          <p data-testid="hero-free-explainer" className="mt-5 text-sm text-body max-w-xl">
            <span className="font-semibold text-ink">Free forever:</span> tailoring, cover letters, downloads and tracking.
          </p>
          <button data-testid="hero-linkedin-cta" onClick={() => { fc.setMode("linkedin"); document.getElementById("free-check")?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
            className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-brand hover:text-brand-hover transition-colors">
            <Linkedin className="w-4 h-4" aria-hidden="true" /> No CV yet? Build one from your LinkedIn profile <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </button>
          <button data-testid="hero-create-profile-cta" onClick={() => window.dispatchEvent(new Event("gi-create-profile"))}
            className="mt-2 flex items-center gap-2 text-left text-sm font-semibold text-brand hover:text-brand-hover transition-colors">
            <UserRound className="w-4 h-4 shrink-0" aria-hidden="true" /> No LinkedIn either? Create your profile and build a CV from scratch <ArrowRight className="w-4 h-4 shrink-0" aria-hidden="true" />
          </button>
          <ul data-testid="hero-benefit-indicators" className="mt-5 flex flex-wrap gap-x-6 gap-y-3">
            {BENEFITS.map((b) => (
              <li key={b.label} className="flex items-center gap-2 text-sm font-medium text-ink">
                <b.icon className="w-4 h-4 text-brand" aria-hidden="true" /> {b.label}
              </li>
            ))}
          </ul>
        </div>
      </div>
      {fc.state === "done" && fc.result && <FreeCheckResult result={fc.result} token={localStorage.getItem(GUEST_KEY)} signedIn={Boolean(user)} onSave={save} onReset={fc.reset} onDelete={fc.remove} />}
    </section>
  );
};

const FREE = ["CV tailoring for any job (up to 30 an hour)", "Cover letters, PDF and Word downloads", "Job Compatibility Analysis and Evidence Checker", "Job post scam check and fit check", "Application tracker, Fit Insights and weekly tips", "Refine your CV by voice", "Kesh, your AI assistant", "Privacy & Data controls: export or delete anytime"];
const PAID = ["Guided Career Journey: find your direction, build stronger applications, keep improving", "Personalised coaching that uses your CV and real application results", "Recruiter outreach drafts and offer negotiation help", "Interview voice practice with spoken feedback", "Saved progress, nudges and a question bank"];

const Item = ({ children }) => (
  <li className="flex gap-3 text-[15px] text-body"><Check className="w-4 h-4 text-brand mt-1 shrink-0" aria-hidden="true" />{children}</li>
);

export const PricingSection = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  return (
    <section id="pricing" data-testid="landing-career-section" className="scroll-mt-20">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 py-20 sm:py-28">
        <div className="max-w-2xl mb-12">
          <div className="gi-eyebrow mb-3">Pricing</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-ink">Core tools are free. Career Journey is optional.</h2>
          <p className="mt-4 text-lg text-body">No hidden charges or countdown timers. Upgrade only if guided coaching would help you.</p>
        </div>
        <div className="grid lg:grid-cols-2 gap-6 items-start">
          <div className="gi-card p-7 sm:p-8" data-testid="pricing-free-card">
            <div className="flex items-baseline justify-between">
              <h3 className="font-display text-xl font-bold text-ink">Free tools</h3>
              <span className="font-display text-3xl font-extrabold text-ink">$0</span>
            </div>
            <p className="text-sm text-body mt-1">Free forever. No card needed.</p>
            <ul className="mt-6 space-y-3">{FREE.map((f) => <Item key={f}>{f}</Item>)}</ul>
            <button data-testid="pricing-free-cta" onClick={() => navigate(user ? "/dashboard" : "/signin")} className="mt-8 w-full gi-btn gi-btn-secondary">Start free</button>
          </div>
          <div className="gi-card p-7 sm:p-8 border-brand/40 ring-1 ring-brand/20" data-testid="pricing-career-card">
            <div className="flex items-center gap-2">
              <Compass className="w-5 h-5 text-brand" aria-hidden="true" />
              <h3 className="font-display text-xl font-bold text-ink">Career Journey</h3>
            </div>
            <div className="mt-5 grid grid-cols-1 min-[420px]:grid-cols-2 gap-3">
              <div className="rounded-xl border border-line bg-canvas p-4" data-testid="pricing-monthly-option">
                <div className="font-display text-2xl font-extrabold text-ink">S$9 <span className="text-sm font-normal text-body">/ month</span></div>
                <div className="text-sm text-body mt-1">Cancel anytime.</div>
              </div>
              <div className="rounded-xl border border-line bg-canvas p-4" data-testid="pricing-lifetime-option">
                <div className="font-display text-2xl font-extrabold text-ink">S$39 <span className="text-sm font-normal text-body">once</span></div>
                <div className="text-sm text-body mt-1">Lifetime access.</div>
              </div>
            </div>
            <ul className="mt-6 space-y-3">{PAID.map((f) => <Item key={f}>{f}</Item>)}</ul>
            <button data-testid="career-cta-button" onClick={() => navigate(user ? "/dashboard" : "/signin")} className="mt-8 w-full gi-btn gi-btn-primary">
              {user ? "Open Career Journey" : "Sign in to unlock"} <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </button>
            <p className="mt-4 text-sm text-faint leading-relaxed" data-testid="pricing-terms">
              Prices in Singapore dollars (SGD). Monthly plans can be cancelled anytime from "Manage subscription". Refunds follow our <a href="/terms" className="text-brand hover:underline">Terms</a>. Fair-use limits may apply to protect the service from automated abuse.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
